// <!-- AI-authored: Darby's Claude -->
import { createExternalStore } from "@client/utils/createExternalStore";
import { useCurrentUser } from "@client/hooks/use-current-user";

// Dev-only round-cache override (see DisableCachingToggle, SP-2637): when on,
// round opens skip the cache-first fast path and download fresh — for testing
// load and cache behavior on a device whose cache is warm, without wiping it.
// Read-side only: the fresh download still writes the cache, so turning the
// override off returns to a warm fast path (the same "downloads are kept"
// stance as forceStreamingStore, which this mirrors). Device-scoped and
// persisted in localStorage — a property of the device, not of the signed-in
// (or impersonated) user.

const STORAGE_KEY = "round-disable-caching";

function readStoredDisableCaching(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    // localStorage can throw (privacy mode / storage disabled); the override
    // is a dev tool, so default to normal behavior.
    return false;
  }
}

const store = createExternalStore<boolean>(readStoredDisableCaching());

// Raw stored value — for the toggle UI's checked state only. Load-path
// consumers must use useDisableCachingActive below.
export function useDisableCaching(): boolean {
  return store.useValue();
}

// Whether the override should actually be APPLIED: the stored flag AND the
// real logged-in user (not the impersonated one) being an SFC admin — the
// same gate that shows the toggle. The flag is device-persisted, so without
// this an admin who left it on (or a de-adminned account) would strand a
// non-admin on the same device paying a full download on every round open,
// with no visible control to turn it off. (The iPad's OFFLINE fallback is a
// separate mechanism — SP-2612 serves the cached aggregate when the download
// fails — so offline opens still work under the override; they just stall on
// the doomed download first.) While user data is still loading the override
// is inactive, which errs toward normal (cached) loads.
export function useDisableCachingActive(): boolean {
  const disableCaching = store.useValue();
  const { data: currentUser } = useCurrentUser();
  return disableCaching && currentUser?.isSfcAdmin === true;
}

export function setDisableCaching(disableCaching: boolean): void {
  store.set(disableCaching);

  try {
    window.localStorage.setItem(STORAGE_KEY, String(disableCaching));
  } catch {
    // Best-effort persistence; the in-memory store still applies app-wide.
  }
}
